import * as z from 'zod';

export const CreateManualTaxMappingRequest = /*#__PURE__*/ z.object({
  manualTaxMapping: z
    .object({
      _id: z
        .string()
        .describe('Manual tax mapping ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the manual tax mapping is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the manual tax mapping.\n\nIgnored when creating a manual tax mapping.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the manual tax mapping was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the manual tax mapping was last updated.')
        .optional()
        .nullable(),
      taxGroupId: z
        .string()
        .describe(
          'Tax group ID. Tax groups define collections of items or services that share the same tax treatment.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        ),
      taxRegionId: z
        .string()
        .describe(
          'Tax region ID. Tax regions represent geographical areas where specific tax rules apply.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        ),
      taxRate: z
        .string()
        .describe(
          'Tax rate to apply for this tax group and region combination.\n\nSpecified as a decimal string representing a fraction, not a percentage. For example, `"0.05"` is 5%, `"0.1"` is 10%, and `"0.08"` is 8%. Don\'t pass a number (`0.05`), an integer (`5`), or a percentage string (`"5%"`). Up to 6 decimal places are supported.'
        ),
      description: z
        .string()
        .describe(
          'Description of the manual tax mapping. Use this field to add context or notes about the tax rate.'
        )
        .max(1000)
        .optional()
        .nullable(),
      taxType: z
        .string()
        .describe(
          "Type of tax being applied, such as `VAT`, `GST`, or `Sales Tax`. This depends on the jurisdiction's tax laws."
        )
        .max(200)
        .optional(),
      taxName: z
        .string()
        .describe(
          "Customer-facing name of the tax, shown at checkout. For example, `STATE TAX`, `GST`, or `VAT`.\n\nCosmetic only. It doesn't affect the calculated amount. Can be left empty, in which case checkout shows a default label such as `Sales Tax`."
        )
        .max(200)
        .optional(),
      jurisdiction: z
        .string()
        .describe(
          'Name of the jurisdiction where this tax rate applies. For example, `California` or `United Kingdom`.'
        )
        .max(200)
        .optional(),
      jurisdictionType: z
        .enum(['UNDEFINED', 'COUNTRY', 'STATE', 'COUNTY', 'CITY', 'SPECIAL'])
        .optional(),
      exemptRates: z
        .array(
          z.object({
            taxExemptGroupId: z
              .string()
              .describe('Tax exempt group ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional(),
            taxRate: z
              .string()
              .describe('Tax override rate for given tax mapping.')
              .optional(),
          })
        )
        .max(50)
        .optional(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe('Extended fields for storing additional custom data.')
        .optional(),
    })
    .describe('Manual tax mapping to create.'),
});
export const CreateManualTaxMappingResponse = /*#__PURE__*/ z.object({
  _id: z
    .string()
    .describe('Manual tax mapping ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the manual tax mapping is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the manual tax mapping.\n\nIgnored when creating a manual tax mapping.'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe('Date and time the manual tax mapping was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the manual tax mapping was last updated.')
    .optional()
    .nullable(),
  taxGroupId: z
    .string()
    .describe(
      'Tax group ID. Tax groups define collections of items or services that share the same tax treatment.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  taxRegionId: z
    .string()
    .describe(
      'Tax region ID. Tax regions represent geographical areas where specific tax rules apply.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  taxRate: z
    .string()
    .describe(
      'Tax rate to apply for this tax group and region combination.\n\nSpecified as a decimal string representing a fraction, not a percentage. For example, `"0.05"` is 5%, `"0.1"` is 10%, and `"0.08"` is 8%. Don\'t pass a number (`0.05`), an integer (`5`), or a percentage string (`"5%"`). Up to 6 decimal places are supported.'
    )
    .optional(),
  description: z
    .string()
    .describe(
      'Description of the manual tax mapping. Use this field to add context or notes about the tax rate.'
    )
    .max(1000)
    .optional()
    .nullable(),
  taxType: z
    .string()
    .describe(
      "Type of tax being applied, such as `VAT`, `GST`, or `Sales Tax`. This depends on the jurisdiction's tax laws."
    )
    .max(200)
    .optional(),
  taxName: z
    .string()
    .describe(
      "Customer-facing name of the tax, shown at checkout. For example, `STATE TAX`, `GST`, or `VAT`.\n\nCosmetic only. It doesn't affect the calculated amount. Can be left empty, in which case checkout shows a default label such as `Sales Tax`."
    )
    .max(200)
    .optional(),
  jurisdiction: z
    .string()
    .describe(
      'Name of the jurisdiction where this tax rate applies. For example, `California` or `United Kingdom`.'
    )
    .max(200)
    .optional(),
  jurisdictionType: z
    .enum(['UNDEFINED', 'COUNTRY', 'STATE', 'COUNTY', 'CITY', 'SPECIAL'])
    .describe(
      'Type of jurisdiction where this tax rate applies, such as country, state, county, city, or special district.'
    )
    .optional(),
  exemptRates: z
    .array(
      z.object({
        taxExemptGroupId: z
          .string()
          .describe('Tax exempt group ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        taxRate: z
          .string()
          .describe('Tax override rate for given tax mapping.')
          .optional(),
      })
    )
    .max(50)
    .optional(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe('Extended fields for storing additional custom data.')
    .optional(),
});
export const BulkCreateManualTaxMappingsRequest = /*#__PURE__*/ z.object({
  manualTaxMappings: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Manual tax mapping ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the manual tax mapping is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the manual tax mapping.\n\nIgnored when creating a manual tax mapping.'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe('Date and time the manual tax mapping was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the manual tax mapping was last updated.')
          .optional()
          .nullable(),
        taxGroupId: z
          .string()
          .describe(
            'Tax group ID. Tax groups define collections of items or services that share the same tax treatment.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          ),
        taxRegionId: z
          .string()
          .describe(
            'Tax region ID. Tax regions represent geographical areas where specific tax rules apply.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          ),
        taxRate: z
          .string()
          .describe(
            'Tax rate to apply for this tax group and region combination.\n\nSpecified as a decimal string representing a fraction, not a percentage. For example, `"0.05"` is 5%, `"0.1"` is 10%, and `"0.08"` is 8%. Don\'t pass a number (`0.05`), an integer (`5`), or a percentage string (`"5%"`). Up to 6 decimal places are supported.'
          ),
        description: z
          .string()
          .describe(
            'Description of the manual tax mapping. Use this field to add context or notes about the tax rate.'
          )
          .max(1000)
          .optional()
          .nullable(),
        taxType: z
          .string()
          .describe(
            "Type of tax being applied, such as `VAT`, `GST`, or `Sales Tax`. This depends on the jurisdiction's tax laws."
          )
          .max(200)
          .optional(),
        taxName: z
          .string()
          .describe(
            "Customer-facing name of the tax, shown at checkout. For example, `STATE TAX`, `GST`, or `VAT`.\n\nCosmetic only. It doesn't affect the calculated amount. Can be left empty, in which case checkout shows a default label such as `Sales Tax`."
          )
          .max(200)
          .optional(),
        jurisdiction: z
          .string()
          .describe(
            'Name of the jurisdiction where this tax rate applies. For example, `California` or `United Kingdom`.'
          )
          .max(200)
          .optional(),
        jurisdictionType: z
          .enum(['UNDEFINED', 'COUNTRY', 'STATE', 'COUNTY', 'CITY', 'SPECIAL'])
          .optional(),
        exemptRates: z
          .array(
            z.object({
              taxExemptGroupId: z
                .string()
                .describe('Tax exempt group ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              taxRate: z
                .string()
                .describe('Tax override rate for given tax mapping.')
                .optional(),
            })
          )
          .max(50)
          .optional(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe('Extended fields for storing additional custom data.')
          .optional(),
      })
    )
    .min(1)
    .max(100),
  options: z
    .object({
      returnEntity: z
        .boolean()
        .describe(
          'Whether to return the created manual tax mapping entities in the response.\n\nDefault: `false`'
        )
        .optional(),
    })
    .optional(),
});
export const BulkCreateManualTaxMappingsResponse = /*#__PURE__*/ z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Provided only whenever possible. For example, `itemId` can't be provided when item creation has failed."
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .min(0)
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action for this item was successful. When `false`, the `error` field is returned.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Metadata for the individual create operation.')
          .optional(),
        item: z
          .object({
            _id: z
              .string()
              .describe('Manual tax mapping ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            revision: z
              .string()
              .regex(/^-?\d+$/, 'Must be a valid Int64 string')
              .describe(
                'Revision number, which increments by 1 each time the manual tax mapping is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the manual tax mapping.\n\nIgnored when creating a manual tax mapping.'
              )
              .optional()
              .nullable(),
            _createdDate: z
              .date()
              .describe('Date and time the manual tax mapping was created.')
              .optional()
              .nullable(),
            _updatedDate: z
              .date()
              .describe(
                'Date and time the manual tax mapping was last updated.'
              )
              .optional()
              .nullable(),
            taxGroupId: z
              .string()
              .describe(
                'Tax group ID. Tax groups define collections of items or services that share the same tax treatment.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            taxRegionId: z
              .string()
              .describe(
                'Tax region ID. Tax regions represent geographical areas where specific tax rules apply.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            taxRate: z
              .string()
              .describe(
                'Tax rate to apply for this tax group and region combination.\n\nSpecified as a decimal string representing a fraction, not a percentage. For example, `"0.05"` is 5%, `"0.1"` is 10%, and `"0.08"` is 8%. Don\'t pass a number (`0.05`), an integer (`5`), or a percentage string (`"5%"`). Up to 6 decimal places are supported.'
              )
              .optional(),
            description: z
              .string()
              .describe(
                'Description of the manual tax mapping. Use this field to add context or notes about the tax rate.'
              )
              .max(1000)
              .optional()
              .nullable(),
            taxType: z
              .string()
              .describe(
                "Type of tax being applied, such as `VAT`, `GST`, or `Sales Tax`. This depends on the jurisdiction's tax laws."
              )
              .max(200)
              .optional(),
            taxName: z
              .string()
              .describe(
                "Customer-facing name of the tax, shown at checkout. For example, `STATE TAX`, `GST`, or `VAT`.\n\nCosmetic only. It doesn't affect the calculated amount. Can be left empty, in which case checkout shows a default label such as `Sales Tax`."
              )
              .max(200)
              .optional(),
            jurisdiction: z
              .string()
              .describe(
                'Name of the jurisdiction where this tax rate applies. For example, `California` or `United Kingdom`.'
              )
              .max(200)
              .optional(),
            jurisdictionType: z
              .enum([
                'UNDEFINED',
                'COUNTRY',
                'STATE',
                'COUNTY',
                'CITY',
                'SPECIAL',
              ])
              .describe(
                'Type of jurisdiction where this tax rate applies, such as country, state, county, city, or special district.'
              )
              .optional(),
            exemptRates: z
              .array(
                z.object({
                  taxExemptGroupId: z
                    .string()
                    .describe('Tax exempt group ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                  taxRate: z
                    .string()
                    .describe('Tax override rate for given tax mapping.')
                    .optional(),
                })
              )
              .max(50)
              .optional(),
            extendedFields: z
              .object({
                namespaces: z
                  .record(z.string(), z.record(z.string(), z.any()))
                  .describe(
                    'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                  )
                  .optional(),
              })
              .describe('Extended fields for storing additional custom data.')
              .optional(),
          })
          .describe(
            'Created manual tax mapping. Only present if `returnEntity` was set to `true` in the request.'
          )
          .optional(),
      })
    )
    .min(1)
    .max(100)
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .min(0)
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .min(0)
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .min(0)
        .optional(),
    })
    .describe('Metadata for the bulk create operation.')
    .optional(),
});
export const GetManualTaxMappingRequest = /*#__PURE__*/ z.object({
  manualTaxMappingId: z
    .string()
    .describe('Manual tax mapping ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const GetManualTaxMappingResponse = /*#__PURE__*/ z.object({
  _id: z
    .string()
    .describe('Manual tax mapping ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the manual tax mapping is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the manual tax mapping.\n\nIgnored when creating a manual tax mapping.'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe('Date and time the manual tax mapping was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the manual tax mapping was last updated.')
    .optional()
    .nullable(),
  taxGroupId: z
    .string()
    .describe(
      'Tax group ID. Tax groups define collections of items or services that share the same tax treatment.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  taxRegionId: z
    .string()
    .describe(
      'Tax region ID. Tax regions represent geographical areas where specific tax rules apply.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  taxRate: z
    .string()
    .describe(
      'Tax rate to apply for this tax group and region combination.\n\nSpecified as a decimal string representing a fraction, not a percentage. For example, `"0.05"` is 5%, `"0.1"` is 10%, and `"0.08"` is 8%. Don\'t pass a number (`0.05`), an integer (`5`), or a percentage string (`"5%"`). Up to 6 decimal places are supported.'
    )
    .optional(),
  description: z
    .string()
    .describe(
      'Description of the manual tax mapping. Use this field to add context or notes about the tax rate.'
    )
    .max(1000)
    .optional()
    .nullable(),
  taxType: z
    .string()
    .describe(
      "Type of tax being applied, such as `VAT`, `GST`, or `Sales Tax`. This depends on the jurisdiction's tax laws."
    )
    .max(200)
    .optional(),
  taxName: z
    .string()
    .describe(
      "Customer-facing name of the tax, shown at checkout. For example, `STATE TAX`, `GST`, or `VAT`.\n\nCosmetic only. It doesn't affect the calculated amount. Can be left empty, in which case checkout shows a default label such as `Sales Tax`."
    )
    .max(200)
    .optional(),
  jurisdiction: z
    .string()
    .describe(
      'Name of the jurisdiction where this tax rate applies. For example, `California` or `United Kingdom`.'
    )
    .max(200)
    .optional(),
  jurisdictionType: z
    .enum(['UNDEFINED', 'COUNTRY', 'STATE', 'COUNTY', 'CITY', 'SPECIAL'])
    .describe(
      'Type of jurisdiction where this tax rate applies, such as country, state, county, city, or special district.'
    )
    .optional(),
  exemptRates: z
    .array(
      z.object({
        taxExemptGroupId: z
          .string()
          .describe('Tax exempt group ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        taxRate: z
          .string()
          .describe('Tax override rate for given tax mapping.')
          .optional(),
      })
    )
    .max(50)
    .optional(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe('Extended fields for storing additional custom data.')
    .optional(),
});
export const UpdateManualTaxMappingRequest = /*#__PURE__*/ z.object({
  _id: z
    .string()
    .describe('Manual tax mapping ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  manualTaxMapping: z
    .object({
      _id: z
        .string()
        .describe('Manual tax mapping ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the manual tax mapping is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the manual tax mapping.\n\nIgnored when creating a manual tax mapping.'
        ),
      _createdDate: z
        .date()
        .describe('Date and time the manual tax mapping was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the manual tax mapping was last updated.')
        .optional()
        .nullable(),
      taxGroupId: z
        .string()
        .describe(
          'Tax group ID. Tax groups define collections of items or services that share the same tax treatment.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      taxRegionId: z
        .string()
        .describe(
          'Tax region ID. Tax regions represent geographical areas where specific tax rules apply.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      taxRate: z
        .string()
        .describe(
          'Tax rate to apply for this tax group and region combination.\n\nSpecified as a decimal string representing a fraction, not a percentage. For example, `"0.05"` is 5%, `"0.1"` is 10%, and `"0.08"` is 8%. Don\'t pass a number (`0.05`), an integer (`5`), or a percentage string (`"5%"`). Up to 6 decimal places are supported.'
        )
        .optional(),
      description: z
        .string()
        .describe(
          'Description of the manual tax mapping. Use this field to add context or notes about the tax rate.'
        )
        .max(1000)
        .optional()
        .nullable(),
      taxType: z
        .string()
        .describe(
          "Type of tax being applied, such as `VAT`, `GST`, or `Sales Tax`. This depends on the jurisdiction's tax laws."
        )
        .max(200)
        .optional(),
      taxName: z
        .string()
        .describe(
          "Customer-facing name of the tax, shown at checkout. For example, `STATE TAX`, `GST`, or `VAT`.\n\nCosmetic only. It doesn't affect the calculated amount. Can be left empty, in which case checkout shows a default label such as `Sales Tax`."
        )
        .max(200)
        .optional(),
      jurisdiction: z
        .string()
        .describe(
          'Name of the jurisdiction where this tax rate applies. For example, `California` or `United Kingdom`.'
        )
        .max(200)
        .optional(),
      jurisdictionType: z
        .enum(['UNDEFINED', 'COUNTRY', 'STATE', 'COUNTY', 'CITY', 'SPECIAL'])
        .optional(),
      exemptRates: z
        .array(
          z.object({
            taxExemptGroupId: z
              .string()
              .describe('Tax exempt group ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional(),
            taxRate: z
              .string()
              .describe('Tax override rate for given tax mapping.')
              .optional(),
          })
        )
        .max(50)
        .optional(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe('Extended fields for storing additional custom data.')
        .optional(),
    })
    .describe('Manual tax mapping to update.'),
});
export const UpdateManualTaxMappingResponse = /*#__PURE__*/ z.object({
  _id: z
    .string()
    .describe('Manual tax mapping ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the manual tax mapping is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the manual tax mapping.\n\nIgnored when creating a manual tax mapping.'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe('Date and time the manual tax mapping was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the manual tax mapping was last updated.')
    .optional()
    .nullable(),
  taxGroupId: z
    .string()
    .describe(
      'Tax group ID. Tax groups define collections of items or services that share the same tax treatment.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  taxRegionId: z
    .string()
    .describe(
      'Tax region ID. Tax regions represent geographical areas where specific tax rules apply.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  taxRate: z
    .string()
    .describe(
      'Tax rate to apply for this tax group and region combination.\n\nSpecified as a decimal string representing a fraction, not a percentage. For example, `"0.05"` is 5%, `"0.1"` is 10%, and `"0.08"` is 8%. Don\'t pass a number (`0.05`), an integer (`5`), or a percentage string (`"5%"`). Up to 6 decimal places are supported.'
    )
    .optional(),
  description: z
    .string()
    .describe(
      'Description of the manual tax mapping. Use this field to add context or notes about the tax rate.'
    )
    .max(1000)
    .optional()
    .nullable(),
  taxType: z
    .string()
    .describe(
      "Type of tax being applied, such as `VAT`, `GST`, or `Sales Tax`. This depends on the jurisdiction's tax laws."
    )
    .max(200)
    .optional(),
  taxName: z
    .string()
    .describe(
      "Customer-facing name of the tax, shown at checkout. For example, `STATE TAX`, `GST`, or `VAT`.\n\nCosmetic only. It doesn't affect the calculated amount. Can be left empty, in which case checkout shows a default label such as `Sales Tax`."
    )
    .max(200)
    .optional(),
  jurisdiction: z
    .string()
    .describe(
      'Name of the jurisdiction where this tax rate applies. For example, `California` or `United Kingdom`.'
    )
    .max(200)
    .optional(),
  jurisdictionType: z
    .enum(['UNDEFINED', 'COUNTRY', 'STATE', 'COUNTY', 'CITY', 'SPECIAL'])
    .describe(
      'Type of jurisdiction where this tax rate applies, such as country, state, county, city, or special district.'
    )
    .optional(),
  exemptRates: z
    .array(
      z.object({
        taxExemptGroupId: z
          .string()
          .describe('Tax exempt group ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        taxRate: z
          .string()
          .describe('Tax override rate for given tax mapping.')
          .optional(),
      })
    )
    .max(50)
    .optional(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe('Extended fields for storing additional custom data.')
    .optional(),
});
export const DeleteManualTaxMappingRequest = /*#__PURE__*/ z.object({
  manualTaxMappingId: z
    .string()
    .describe('Manual tax mapping ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const DeleteManualTaxMappingResponse = /*#__PURE__*/ z.object({});
export const QueryManualTaxMappingsRequest = /*#__PURE__*/ z.object({
  query: z
    .intersection(
      z.object({
        filter: z
          .record(z.string(), z.any())
          .describe(
            'Filter object.\n\nLearn more about the [filter section](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#the-filter-section).'
          )
          .optional()
          .nullable(),
        sort: z
          .array(
            z.object({
              fieldName: z
                .string()
                .describe('Name of the field to sort by.')
                .max(50)
                .optional(),
              order: z.enum(['ASC', 'DESC']).optional(),
            })
          )
          .max(5)
          .optional(),
      }),
      z.xor([
        z.object({ cursorPaging: z.never().optional() }),
        z.object({
          cursorPaging: z
            .object({
              limit: z
                .number()
                .int()
                .describe('Maximum number of items to return in the results.')
                .min(0)
                .max(100)
                .optional()
                .nullable(),
              cursor: z
                .string()
                .describe(
                  "Pointer to the next or previous page in the list of results.\n\nPass the relevant cursor token from the `pagingMetadata` object in the previous call's response.\nNot relevant for the first request."
                )
                .max(16000)
                .optional()
                .nullable(),
            })
            .describe(
              'Cursor paging options.\n\nLearn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).'
            ),
        }),
      ])
    )
    .describe('Query options.'),
});
export const QueryManualTaxMappingsResponse = /*#__PURE__*/ z.object({
  manualTaxMappings: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Manual tax mapping ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the manual tax mapping is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the manual tax mapping.\n\nIgnored when creating a manual tax mapping.'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe('Date and time the manual tax mapping was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the manual tax mapping was last updated.')
          .optional()
          .nullable(),
        taxGroupId: z
          .string()
          .describe(
            'Tax group ID. Tax groups define collections of items or services that share the same tax treatment.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        taxRegionId: z
          .string()
          .describe(
            'Tax region ID. Tax regions represent geographical areas where specific tax rules apply.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        taxRate: z
          .string()
          .describe(
            'Tax rate to apply for this tax group and region combination.\n\nSpecified as a decimal string representing a fraction, not a percentage. For example, `"0.05"` is 5%, `"0.1"` is 10%, and `"0.08"` is 8%. Don\'t pass a number (`0.05`), an integer (`5`), or a percentage string (`"5%"`). Up to 6 decimal places are supported.'
          )
          .optional(),
        description: z
          .string()
          .describe(
            'Description of the manual tax mapping. Use this field to add context or notes about the tax rate.'
          )
          .max(1000)
          .optional()
          .nullable(),
        taxType: z
          .string()
          .describe(
            "Type of tax being applied, such as `VAT`, `GST`, or `Sales Tax`. This depends on the jurisdiction's tax laws."
          )
          .max(200)
          .optional(),
        taxName: z
          .string()
          .describe(
            "Customer-facing name of the tax, shown at checkout. For example, `STATE TAX`, `GST`, or `VAT`.\n\nCosmetic only. It doesn't affect the calculated amount. Can be left empty, in which case checkout shows a default label such as `Sales Tax`."
          )
          .max(200)
          .optional(),
        jurisdiction: z
          .string()
          .describe(
            'Name of the jurisdiction where this tax rate applies. For example, `California` or `United Kingdom`.'
          )
          .max(200)
          .optional(),
        jurisdictionType: z
          .enum(['UNDEFINED', 'COUNTRY', 'STATE', 'COUNTY', 'CITY', 'SPECIAL'])
          .describe(
            'Type of jurisdiction where this tax rate applies, such as country, state, county, city, or special district.'
          )
          .optional(),
        exemptRates: z
          .array(
            z.object({
              taxExemptGroupId: z
                .string()
                .describe('Tax exempt group ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              taxRate: z
                .string()
                .describe('Tax override rate for given tax mapping.')
                .optional(),
            })
          )
          .max(50)
          .optional(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe('Extended fields for storing additional custom data.')
          .optional(),
      })
    )
    .max(100)
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in current page.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursor strings that point to the next page, previous page, or both.'
        )
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Whether there are more pages to retrieve following the current page.\n\n+ `true`: Another page of results can be retrieved.\n+ `false`: This is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe('Pagination metadata.')
    .optional(),
});
export const UpdateExtendedFieldsRequest = /*#__PURE__*/ z.object({
  _id: z.string().describe('ID of the entity to update.'),
  namespace: z
    .string()
    .describe(
      'Identifier for the app whose extended fields are being updated.'
    ),
  options: z.object({
    namespaceData: z
      .record(z.string(), z.any())
      .describe(
        'Data to update. Structured according to the [schema](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields#json-schema-for-extended-fields) defined when the extended fields were configured.'
      ),
  }),
});
export const UpdateExtendedFieldsResponse = /*#__PURE__*/ z.object({
  manualTaxMapping: z
    .object({
      _id: z
        .string()
        .describe('Manual tax mapping ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the manual tax mapping is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the manual tax mapping.\n\nIgnored when creating a manual tax mapping.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the manual tax mapping was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the manual tax mapping was last updated.')
        .optional()
        .nullable(),
      taxGroupId: z
        .string()
        .describe(
          'Tax group ID. Tax groups define collections of items or services that share the same tax treatment.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      taxRegionId: z
        .string()
        .describe(
          'Tax region ID. Tax regions represent geographical areas where specific tax rules apply.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      taxRate: z
        .string()
        .describe(
          'Tax rate to apply for this tax group and region combination.\n\nSpecified as a decimal string representing a fraction, not a percentage. For example, `"0.05"` is 5%, `"0.1"` is 10%, and `"0.08"` is 8%. Don\'t pass a number (`0.05`), an integer (`5`), or a percentage string (`"5%"`). Up to 6 decimal places are supported.'
        )
        .optional(),
      description: z
        .string()
        .describe(
          'Description of the manual tax mapping. Use this field to add context or notes about the tax rate.'
        )
        .max(1000)
        .optional()
        .nullable(),
      taxType: z
        .string()
        .describe(
          "Type of tax being applied, such as `VAT`, `GST`, or `Sales Tax`. This depends on the jurisdiction's tax laws."
        )
        .max(200)
        .optional(),
      taxName: z
        .string()
        .describe(
          "Customer-facing name of the tax, shown at checkout. For example, `STATE TAX`, `GST`, or `VAT`.\n\nCosmetic only. It doesn't affect the calculated amount. Can be left empty, in which case checkout shows a default label such as `Sales Tax`."
        )
        .max(200)
        .optional(),
      jurisdiction: z
        .string()
        .describe(
          'Name of the jurisdiction where this tax rate applies. For example, `California` or `United Kingdom`.'
        )
        .max(200)
        .optional(),
      jurisdictionType: z
        .enum(['UNDEFINED', 'COUNTRY', 'STATE', 'COUNTY', 'CITY', 'SPECIAL'])
        .describe(
          'Type of jurisdiction where this tax rate applies, such as country, state, county, city, or special district.'
        )
        .optional(),
      exemptRates: z
        .array(
          z.object({
            taxExemptGroupId: z
              .string()
              .describe('Tax exempt group ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional(),
            taxRate: z
              .string()
              .describe('Tax override rate for given tax mapping.')
              .optional(),
          })
        )
        .max(50)
        .optional(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe('Extended fields for storing additional custom data.')
        .optional(),
    })
    .describe('Updated manual tax mapping.')
    .optional(),
});
export const BulkAssignExemptOverridesRequest = /*#__PURE__*/ z.object({
  options: z
    .object({
      assignOverrides: z
        .array(
          z.object({
            manualTaxMappingId: z
              .string()
              .describe(
                'ID of the manual tax mapping to which the exempt group override will be assigned.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              ),
            exemptGroupOverrides: z
              .array(
                z.object({
                  exemptGroupId: z
                    .string()
                    .describe('ID of the tax exempt group.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    ),
                  rate: z
                    .string()
                    .describe(
                      'Override tax rate for the exempt group in the context of the manual tax mapping.'
                    ),
                })
              )
              .min(1)
              .max(50),
          })
        )
        .max(50)
        .optional(),
      returnEntity: z
        .boolean()
        .describe(
          'Whether to return the updated manual tax mapping entities in the response.\nDefault: `false`'
        )
        .optional(),
    })
    .optional(),
});
export const BulkAssignExemptOverridesResponse = /*#__PURE__*/ z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Provided only whenever possible. For example, `itemId` can't be provided when item creation has failed."
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .min(0)
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action for this item was successful. When `false`, the `error` field is returned.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Metadata for the individual assign operation.')
          .optional(),
        item: z
          .object({
            _id: z
              .string()
              .describe('Manual tax mapping ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            revision: z
              .string()
              .regex(/^-?\d+$/, 'Must be a valid Int64 string')
              .describe(
                'Revision number, which increments by 1 each time the manual tax mapping is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the manual tax mapping.\n\nIgnored when creating a manual tax mapping.'
              )
              .optional()
              .nullable(),
            _createdDate: z
              .date()
              .describe('Date and time the manual tax mapping was created.')
              .optional()
              .nullable(),
            _updatedDate: z
              .date()
              .describe(
                'Date and time the manual tax mapping was last updated.'
              )
              .optional()
              .nullable(),
            taxGroupId: z
              .string()
              .describe(
                'Tax group ID. Tax groups define collections of items or services that share the same tax treatment.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            taxRegionId: z
              .string()
              .describe(
                'Tax region ID. Tax regions represent geographical areas where specific tax rules apply.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            taxRate: z
              .string()
              .describe(
                'Tax rate to apply for this tax group and region combination.\n\nSpecified as a decimal string representing a fraction, not a percentage. For example, `"0.05"` is 5%, `"0.1"` is 10%, and `"0.08"` is 8%. Don\'t pass a number (`0.05`), an integer (`5`), or a percentage string (`"5%"`). Up to 6 decimal places are supported.'
              )
              .optional(),
            description: z
              .string()
              .describe(
                'Description of the manual tax mapping. Use this field to add context or notes about the tax rate.'
              )
              .max(1000)
              .optional()
              .nullable(),
            taxType: z
              .string()
              .describe(
                "Type of tax being applied, such as `VAT`, `GST`, or `Sales Tax`. This depends on the jurisdiction's tax laws."
              )
              .max(200)
              .optional(),
            taxName: z
              .string()
              .describe(
                "Customer-facing name of the tax, shown at checkout. For example, `STATE TAX`, `GST`, or `VAT`.\n\nCosmetic only. It doesn't affect the calculated amount. Can be left empty, in which case checkout shows a default label such as `Sales Tax`."
              )
              .max(200)
              .optional(),
            jurisdiction: z
              .string()
              .describe(
                'Name of the jurisdiction where this tax rate applies. For example, `California` or `United Kingdom`.'
              )
              .max(200)
              .optional(),
            jurisdictionType: z
              .enum([
                'UNDEFINED',
                'COUNTRY',
                'STATE',
                'COUNTY',
                'CITY',
                'SPECIAL',
              ])
              .describe(
                'Type of jurisdiction where this tax rate applies, such as country, state, county, city, or special district.'
              )
              .optional(),
            exemptRates: z
              .array(
                z.object({
                  taxExemptGroupId: z
                    .string()
                    .describe('Tax exempt group ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                  taxRate: z
                    .string()
                    .describe('Tax override rate for given tax mapping.')
                    .optional(),
                })
              )
              .max(50)
              .optional(),
            extendedFields: z
              .object({
                namespaces: z
                  .record(z.string(), z.record(z.string(), z.any()))
                  .describe(
                    'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                  )
                  .optional(),
              })
              .describe('Extended fields for storing additional custom data.')
              .optional(),
          })
          .describe(
            'Updated manual tax mapping. Only present if `returnEntity` was set to `true` in the request.'
          )
          .optional(),
      })
    )
    .max(50)
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .min(0)
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .min(0)
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .min(0)
        .optional(),
    })
    .describe('Metadata for the bulk assign operation.')
    .optional(),
});
export const BulkUnassignExemptOverridesRequest = /*#__PURE__*/ z.object({
  unassignOverrides: z
    .array(
      z.object({
        manualTaxMappingId: z
          .string()
          .describe(
            'ID of the manual tax mapping from which the exempt group override will be unassigned.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          ),
        exemptGroupIds: z.array(z.string()).min(1).max(50).optional(),
      })
    )
    .max(50),
  options: z
    .object({
      returnEntity: z
        .boolean()
        .describe(
          'Whether to return the updated manual tax mapping entities in the response.\nDefault: `false`'
        )
        .optional(),
    })
    .optional(),
});
export const BulkUnassignExemptOverridesResponse = /*#__PURE__*/ z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Provided only whenever possible. For example, `itemId` can't be provided when item creation has failed."
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .min(0)
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action for this item was successful. When `false`, the `error` field is returned.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Metadata for the individual assign operation.')
          .optional(),
        item: z
          .object({
            _id: z
              .string()
              .describe('Manual tax mapping ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            revision: z
              .string()
              .regex(/^-?\d+$/, 'Must be a valid Int64 string')
              .describe(
                'Revision number, which increments by 1 each time the manual tax mapping is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the manual tax mapping.\n\nIgnored when creating a manual tax mapping.'
              )
              .optional()
              .nullable(),
            _createdDate: z
              .date()
              .describe('Date and time the manual tax mapping was created.')
              .optional()
              .nullable(),
            _updatedDate: z
              .date()
              .describe(
                'Date and time the manual tax mapping was last updated.'
              )
              .optional()
              .nullable(),
            taxGroupId: z
              .string()
              .describe(
                'Tax group ID. Tax groups define collections of items or services that share the same tax treatment.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            taxRegionId: z
              .string()
              .describe(
                'Tax region ID. Tax regions represent geographical areas where specific tax rules apply.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            taxRate: z
              .string()
              .describe(
                'Tax rate to apply for this tax group and region combination.\n\nSpecified as a decimal string representing a fraction, not a percentage. For example, `"0.05"` is 5%, `"0.1"` is 10%, and `"0.08"` is 8%. Don\'t pass a number (`0.05`), an integer (`5`), or a percentage string (`"5%"`). Up to 6 decimal places are supported.'
              )
              .optional(),
            description: z
              .string()
              .describe(
                'Description of the manual tax mapping. Use this field to add context or notes about the tax rate.'
              )
              .max(1000)
              .optional()
              .nullable(),
            taxType: z
              .string()
              .describe(
                "Type of tax being applied, such as `VAT`, `GST`, or `Sales Tax`. This depends on the jurisdiction's tax laws."
              )
              .max(200)
              .optional(),
            taxName: z
              .string()
              .describe(
                "Customer-facing name of the tax, shown at checkout. For example, `STATE TAX`, `GST`, or `VAT`.\n\nCosmetic only. It doesn't affect the calculated amount. Can be left empty, in which case checkout shows a default label such as `Sales Tax`."
              )
              .max(200)
              .optional(),
            jurisdiction: z
              .string()
              .describe(
                'Name of the jurisdiction where this tax rate applies. For example, `California` or `United Kingdom`.'
              )
              .max(200)
              .optional(),
            jurisdictionType: z
              .enum([
                'UNDEFINED',
                'COUNTRY',
                'STATE',
                'COUNTY',
                'CITY',
                'SPECIAL',
              ])
              .describe(
                'Type of jurisdiction where this tax rate applies, such as country, state, county, city, or special district.'
              )
              .optional(),
            exemptRates: z
              .array(
                z.object({
                  taxExemptGroupId: z
                    .string()
                    .describe('Tax exempt group ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                  taxRate: z
                    .string()
                    .describe('Tax override rate for given tax mapping.')
                    .optional(),
                })
              )
              .max(50)
              .optional(),
            extendedFields: z
              .object({
                namespaces: z
                  .record(z.string(), z.record(z.string(), z.any()))
                  .describe(
                    'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                  )
                  .optional(),
              })
              .describe('Extended fields for storing additional custom data.')
              .optional(),
          })
          .describe(
            'Updated manual tax mapping. Only present if `returnEntity` was set to `true` in the request.'
          )
          .optional(),
      })
    )
    .max(10)
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .min(0)
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .min(0)
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .min(0)
        .optional(),
    })
    .describe('Metadata for the bulk assign operation.')
    .optional(),
});
export const ListManualTaxMappingsRequest = /*#__PURE__*/ z.object({});
export const ListManualTaxMappingsResponse = /*#__PURE__*/ z.object({
  manualTaxMappings: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Manual tax mapping ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the manual tax mapping is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the manual tax mapping.\n\nIgnored when creating a manual tax mapping.'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe('Date and time the manual tax mapping was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the manual tax mapping was last updated.')
          .optional()
          .nullable(),
        taxGroupId: z
          .string()
          .describe(
            'Tax group ID. Tax groups define collections of items or services that share the same tax treatment.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        taxRegionId: z
          .string()
          .describe(
            'Tax region ID. Tax regions represent geographical areas where specific tax rules apply.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        taxRate: z
          .string()
          .describe(
            'Tax rate to apply for this tax group and region combination.\n\nSpecified as a decimal string representing a fraction, not a percentage. For example, `"0.05"` is 5%, `"0.1"` is 10%, and `"0.08"` is 8%. Don\'t pass a number (`0.05`), an integer (`5`), or a percentage string (`"5%"`). Up to 6 decimal places are supported.'
          )
          .optional(),
        description: z
          .string()
          .describe(
            'Description of the manual tax mapping. Use this field to add context or notes about the tax rate.'
          )
          .max(1000)
          .optional()
          .nullable(),
        taxType: z
          .string()
          .describe(
            "Type of tax being applied, such as `VAT`, `GST`, or `Sales Tax`. This depends on the jurisdiction's tax laws."
          )
          .max(200)
          .optional(),
        taxName: z
          .string()
          .describe(
            "Customer-facing name of the tax, shown at checkout. For example, `STATE TAX`, `GST`, or `VAT`.\n\nCosmetic only. It doesn't affect the calculated amount. Can be left empty, in which case checkout shows a default label such as `Sales Tax`."
          )
          .max(200)
          .optional(),
        jurisdiction: z
          .string()
          .describe(
            'Name of the jurisdiction where this tax rate applies. For example, `California` or `United Kingdom`.'
          )
          .max(200)
          .optional(),
        jurisdictionType: z
          .enum(['UNDEFINED', 'COUNTRY', 'STATE', 'COUNTY', 'CITY', 'SPECIAL'])
          .describe(
            'Type of jurisdiction where this tax rate applies, such as country, state, county, city, or special district.'
          )
          .optional(),
        exemptRates: z
          .array(
            z.object({
              taxExemptGroupId: z
                .string()
                .describe('Tax exempt group ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              taxRate: z
                .string()
                .describe('Tax override rate for given tax mapping.')
                .optional(),
            })
          )
          .max(50)
          .optional(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe('Extended fields for storing additional custom data.')
          .optional(),
      })
    )
    .max(1000)
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in current page.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursor strings that point to the next page, previous page, or both.'
        )
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Whether there are more pages to retrieve following the current page.\n\n+ `true`: Another page of results can be retrieved.\n+ `false`: This is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe('Pagination metadata.')
    .optional(),
});
